import { useGetOrganization$, useGetUserClient$ } from 'dataQueries'
import { ClientType, PlatformUserBase, PreferredPaymentMethodType, SubscriptionPlan } from 'models/user'
import { useEffect, useMemo, useState } from 'react'

import { useUserData } from 'components/contexts/UserDataContext'
import { CommonStatusCode } from 'constants/API'
import { FINISHED_QUERY_STATES } from 'constants/misc'
import { PFClientSelectionError } from 'constants/purchaseFlow'
import constate from 'constate'
import { Nullable } from 'models/helpers'
import { ActiveSubscriptionStatuses } from 'models/user/userWorkspaceDTOs'
import { useAuth0 } from 'utils/auth'
import { getUserTimezone } from 'utils/time'
import { UserIsPlatformUserClient } from 'utils/typeguards'

/** Status code returned when user has a different role than expected */
const differentRoleStatus = 422

/**
 * Describes the user for whom the order is being created.
 *
 * Extends {@link PlatformUserBase} with purchase-flow-specific properties.
 */
export interface PurchaseFlowTargetOrderUser extends PlatformUserBase {
  /** Whether the user is subscribed (member of at least one non-personal workspace) */
  isSubscribed: boolean

  /** Preferred payment method of the user */
  preferredPaymentMethod: Nullable<PreferredPaymentMethodType>

  /** Client type of the user */
  clientType: Nullable<ClientType>

  /** VAT number of the user */
  vatNumber: Nullable<string>

  /** User timezone resolved from profile or browser fallback */
  timezone: string

  /** Whether the user is allowed to pay by invoice */
  invoiceAllowed: boolean

  /** Whether a Power of Attorney document is required for representatives */
  requiresRepresentativePowerOfAttorney: boolean
}

/**
 * Context provider and hook for resolving the target user
 * for whom the purchase flow order is being placed.
 *
 * Supports:
 * - User ordering for themselves
 * - Admin ordering on behalf of another user
 * - Validation of client eligibility for ordering
 */
export const [TargetOrderUserProvider, useTargetOrderUser] = constate(() => {

  /** Authentication roles of the currently logged-in user */
  const { roles } = useAuth0()

  /** Base user, client, and organization data of the logged-in user */
  const { hasWorkspaceActiveSubscription, baseUserData, clientData, organizationData, userPropertyWorkspaceId } = useUserData()

  /**
   * Email of the client selected by admin for ordering on behalf
   */
  const [adminSelectedUserEmail, setAdminSelectedUserEmail] = useState<string>('')

  /**
   * Workspace ID selected by admin for placing the order
   */
  const [adminSelectedWorkspaceId, setAdminSelectedWorkspaceId] = useState<string>('')

  /** Query for fetching client user data */
  const getPFClient = useGetUserClient$()

  /** Query for fetching organization data */
  const getPFOrganization = useGetOrganization$()

  /**
   * Active workspaces of the fetched client that can be used for ordering.
   */
  const activeClientWorkspaces = useMemo(() => {
    const userData = getPFClient.data?.data
    if (!userData || !UserIsPlatformUserClient(userData)) return []

    return userData.workspaces.filter(
      ws => ActiveSubscriptionStatuses.has(ws.status) && ws.isMember
    )
  }, [getPFClient.data?.data])

  /** Reset workspace selection when client data changes */
  useEffect(() => {
    setAdminSelectedWorkspaceId('')
  }, [getPFClient.data?.data])

  /**
   * Computes error state related to client selection.
   *
   * @remarks
   * Determines whether the admin is allowed to place an order
   * on behalf of the selected user.
   */
  const clientSelectionError = useMemo(
    () => {
      const userData = getPFClient.data?.data
      const requestStatus = getPFClient.data?.status

      if (!FINISHED_QUERY_STATES.has(getPFClient.status)) return PFClientSelectionError.NO_DATA

      if (getPFClient.isError && getPFClient.error.response?.status === CommonStatusCode.NOT_FOUND) {
        return PFClientSelectionError.NOT_FOUND
      }

      if (!userData) return PFClientSelectionError.NOT_FOUND

      if (requestStatus === differentRoleStatus) return PFClientSelectionError.NOT_CLIENT

      // The client's own preferred payment method does not stand in the way: an admin is only ever
      // offered invoice for the order (the backend withholds card and SEPA from admins).
      return undefined
    },
    [getPFClient.data?.data, getPFClient.data?.status, getPFClient.error?.response?.status, getPFClient.isError, getPFClient.status]
  )

  /**
   * Target user for whom the order is being placed.
   *
   * @remarks
   * - For clients: resolves to the logged-in user
   * - For admins: resolves to the selected client user
   *
   * **Due to technical constraints, admins are treated as clients.**
   */
  const targetUser = useMemo<PurchaseFlowTargetOrderUser>(
    () => {

      const userMeBasedTargetUser: PurchaseFlowTargetOrderUser = {
        ...baseUserData,
        isSubscribed: hasWorkspaceActiveSubscription,
        preferredPaymentMethod: clientData?.preferredPaymentMethod,
        clientType: clientData?.clientType,
        vatNumber: clientData?.vatNumber,
        timezone: getUserTimezone(baseUserData),
        invoiceAllowed: hasWorkspaceActiveSubscription || !!organizationData?.invoiceAllowed,
        requiresRepresentativePowerOfAttorney: !!organizationData?.requirePowerOfAttorneyRepresentatives,
      }

      const fetchedUserData = getPFClient.data?.data

      if (roles.isClient) return userMeBasedTargetUser
      if (!fetchedUserData) return userMeBasedTargetUser
      if (fetchedUserData.email === baseUserData?.email) return userMeBasedTargetUser

      // TYPEGUARD ONLY - provide most restrictive values
      if (!UserIsPlatformUserClient(fetchedUserData)) {
        return {
          isSubscribed: false,
          preferredPaymentMethod: null,
          vatNumber: null,
          clientType: null,
          timezone: getUserTimezone(),
          invoiceAllowed: false,
          requiresRepresentativePowerOfAttorney: false,
        }
      }

      const {
        id,
        name,
        email,
        phone,
        country,
        defaultTimezone,
        language,
        preferredPaymentMethod,
        vatNumber,
        clientType,
        workspaces
      } = fetchedUserData

      return {
        id,
        name,
        email,
        phone,
        country,
        defaultTimezone,
        language,
        preferredPaymentMethod,
        vatNumber,
        clientType,
        isSubscribed: !!workspaces.find(
          workspace =>
            workspace.plan !== SubscriptionPlan.PERSONAL &&
            ActiveSubscriptionStatuses.has(workspace.status) &&
            workspace.isMember
        ),
        timezone: getUserTimezone(fetchedUserData),
        invoiceAllowed: !!getPFOrganization.data?.data?.invoiceAllowed,
        requiresRepresentativePowerOfAttorney: !!getPFOrganization.data?.data?.requirePowerOfAttorneyRepresentatives
      }

    },
    [
      baseUserData,
      hasWorkspaceActiveSubscription,
      clientData?.preferredPaymentMethod,
      clientData?.clientType,
      clientData?.vatNumber,
      organizationData?.invoiceAllowed,
      organizationData?.requirePowerOfAttorneyRepresentatives,
      getPFClient.data?.data,
      roles.isClient,
      getPFOrganization.data?.data?.invoiceAllowed,
      getPFOrganization.data?.data?.requirePowerOfAttorneyRepresentatives
    ]
  )

  /** Workspace the order is placed in: the admin's pick when ordering on behalf, else the user's current one */
  const orderWorkspaceId = (roles.isAdmin && adminSelectedWorkspaceId) ? adminSelectedWorkspaceId : userPropertyWorkspaceId

  return {
    targetUser,
    adminSelectedUserEmail,
    adminSelectedWorkspaceId,
    orderWorkspaceId,
    activeClientWorkspaces,
    getPFClient,
    clientSelectionError,
    getPFOrganization,
    setAdminSelectedUserEmail,
    setAdminSelectedWorkspaceId,
  }
})
